import { Loader } from "materia"
import {
  Fragment,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import type {
  AttachmentRef,
  MessageStatus,
  PeerProfile,
  SharedItem,
  SharedItemRef,
} from "../../types"
import type { ResolveAttachmentUrl } from "./AttachmentImage"
import { MessageBubble } from "./MessageBubble"
import { RetractConfirm } from "./RetractConfirm"
import { ThreadHeader } from "./ThreadHeader"
import { ThreadIntro } from "./ThreadIntro"
import { ThreadNotice } from "./ThreadNotice"
import { ThreadTimeDivider } from "./ThreadTimeDivider"
import { TypingIndicator } from "./TypingIndicator"
import { isArrival, openThread, type ThreadOpening } from "./arrivals"
import { annotateRuns } from "./threadRuns"
import s from "./Thread.module.scss"

export type ThreadMessageModel = {
  id: string
  body: string
  timestamp: number
  isOwn: boolean
  isRetracted: boolean
  /** The learning item this message shares, rendered as a card (FAME-237). */
  share?: SharedItemRef
  /** Its resolved metadata, once the batch lookup lands (FAME-453). */
  resolvedShare?: SharedItem
  /** The lookup is still in flight. */
  isShareResolving?: boolean
  /** The lookup failed — the card falls back to the sender's deep link. */
  shareResolveFailed?: boolean
  /** The image this message carries, shown above any caption. */
  attachment?: AttachmentRef
  author?: PeerProfile
  authorHandle?: string
  /** Delivery of one of our own sends; absent on everything we received. */
  status?: MessageStatus
  /**
   * Whether to offer a retraction (`chat/retraction.ts`). It decides whether
   * the message carries an actions menu at all — a message nobody can delete
   * has no triple-dot trigger, rather than a disabled item (FAME-687).
   */
  canRetract?: boolean
}

/** Phrased by the caller, which is where handles become names. */
export type ThreadNoticeModel = {
  id: string
  timestamp: number
  notice: string
}

export type ThreadRow = ThreadMessageModel | ThreadNoticeModel

/** Sub-pixel rounding means "at the bottom" is seldom exactly zero. */
const NEAR_BOTTOM_PX = 32

type ThreadProps = {
  name: string
  faces: PeerProfile[]
  memberCount?: number
  isDirect?: boolean
  /** Oldest first. */
  messages: ThreadRow[]
  onBack: () => void
  /** Groups only: the group menu, in the header. */
  menu?: ReactNode
  /** Groups only, and only for those who may add. */
  onAddMembers?: () => void
  onRetract?: (messageId: string) => void
  /** Try a failed send again. */
  onResend?: (messageId: string) => void
  onOpenDm?: (handle: string) => void
  /** Open a shared learning item (FAME-237); the caller owns route + access. */
  onOpenProduct?: (item: SharedItem) => void
  /** Where an image message gets the URL to show its image. */
  resolveAttachmentUrl: ResolveAttachmentUrl
  /** Passed in, so the thread stays about reading. */
  composer?: ReactNode
  /** History has not come back yet, so an empty thread may not be empty. */
  isLoading?: boolean
  /** Who is typing now, in the order they started. */
  typing?: { handle: string; profile: PeerProfile }[]
}

export function Thread({
  name,
  faces,
  memberCount,
  isDirect,
  messages,
  onBack,
  menu,
  onAddMembers,
  onRetract,
  onResend,
  onOpenDm,
  onOpenProduct,
  resolveAttachmentUrl,
  composer,
  isLoading = false,
  typing = [],
}: ThreadProps) {
  const messagesBox = useRef<HTMLDivElement>(null)
  const isAtBottom = useRef(true)
  const lastRowId = useRef<string>()
  const [pendingRetraction, setPendingRetraction] = useState<string>()
  // Runs are the thread's own reading of its rows rather than something every
  // caller has to work out — see `threadRuns.ts`.
  const rows = annotateRuns(messages)
  // Taken once history is in, so a message from the last minute of it does
  // not read as an arrival.
  const opening = useRef<ThreadOpening>()
  if (!opening.current && !isLoading)
    opening.current = openThread(messages.map((row) => row.id))

  /**
   * A thread is read from the bottom. Our own send always returns us there;
   * an arrival, or someone starting to type, only stays in view when we were
   * already reading from the bottom.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: an indicator appearing re-pins too
  useLayoutEffect(() => {
    const box = messagesBox.current
    if (!box || messages.length === 0) return

    const newest = messages[messages.length - 1]
    if (!newest) return
    const isOwnSend =
      newest.id !== lastRowId.current && "isOwn" in newest && newest.isOwn
    lastRowId.current = newest.id

    if (!isOwnSend && !isAtBottom.current) return

    box.scrollTop = box.scrollHeight
    isAtBottom.current = true
  }, [messages, typing.length])

  const rememberScrollPosition = () => {
    const box = messagesBox.current
    if (!box) return

    isAtBottom.current =
      box.scrollHeight - box.scrollTop - box.clientHeight < NEAR_BOTTOM_PX
  }

  return (
    <div className={s.thread}>
      <ThreadHeader
        name={name}
        faces={faces}
        memberCount={memberCount}
        isDirect={isDirect}
        onBack={onBack}
        menu={menu}
      />

      {messages.length === 0 && isLoading ? (
        <div className={s.thread__loading}>
          {/* Delayed, so a quick load shows nothing rather than a flash. */}
          <Loader delay={400} />
        </div>
      ) : messages.length === 0 ? (
        <ThreadIntro
          name={name}
          faces={faces}
          memberCount={memberCount}
          onAddMembers={onAddMembers}
        />
      ) : (
        <div
          className={s.thread__messages}
          ref={messagesBox}
          onScroll={rememberScrollPosition}
        >
          {rows.map((row) =>
            "notice" in row ? (
              <ThreadNotice key={row.id} text={row.notice} />
            ) : (
              <Fragment key={row.id}>
                {row.showsTimeDivider && (
                  <ThreadTimeDivider timestamp={row.timestamp} />
                )}
                <MessageBubble
                  {...row}
                  isArrival={
                    opening.current ? isArrival(row, opening.current) : false
                  }
                  onRetract={
                    onRetract && row.canRetract
                      ? () => setPendingRetraction(row.id)
                      : undefined
                  }
                  onResend={
                    onResend && row.status === "failed"
                      ? () => onResend(row.id)
                      : undefined
                  }
                  onOpenDm={onOpenDm}
                  onOpenProduct={onOpenProduct}
                  resolveAttachmentUrl={resolveAttachmentUrl}
                />
              </Fragment>
            )
          )}

          {typing.map(({ handle, profile }) => (
            <TypingIndicator
              key={handle}
              author={profile}
              showsName={!isDirect}
            />
          ))}
        </div>
      )}

      {composer}

      {pendingRetraction && (
        <RetractConfirm
          onConfirm={() => {
            onRetract?.(pendingRetraction)
            setPendingRetraction(undefined)
          }}
          onCancel={() => setPendingRetraction(undefined)}
        />
      )}
    </div>
  )
}
